import { useEffect, useState } from 'react';
import type { FinancialOverview, HostingServerSummary } from '@tosho/contracts';
import { financialOverviewApi } from './financial-overview-api';
import { financialMoney } from './financial-money';
import { hostingServerApi } from './hosting-server-api';
import { PrototypeIcon } from './PrototypeIcon';

export function dashboardMonth(now: Date): string {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  return (
    parts.find((p) => p.type === 'year')!.value + '-' + parts.find((p) => p.type === 'month')!.value
  );
}
/** Monté uniquement dans la vue Administrateur. Réutiliser les projections
 * réelles, sans copier les calculs Finances ni compter une page de serveurs. */
export function DashboardLiveMetrics(): React.JSX.Element {
  const [month, setMonth] = useState(() => dashboardMonth(new Date()));
  const [revenue, setRevenue] = useState<
    FinancialOverview['indicators']['receivedRevenueHt'] | null
  >(null);
  const [hosting, setHosting] = useState<HostingServerSummary | null>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setMonth(dashboardMonth(new Date())), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    let active = true;
    setRevenue(null);
    void financialOverviewApi
      .read(month)
      .then((view) => {
        const value = view?.period === month ? view.indicators?.receivedRevenueHt : undefined;
        if (
          active &&
          value &&
          ['confirme', 'incomplet', 'non_calculable'].includes(value.confidence) &&
          (value.cents === null || /^-?[0-9]+$/u.test(value.cents))
        )
          setRevenue(value);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [month]);
  useEffect(() => {
    let active = true;
    void hostingServerApi
      .list(0, 5)
      .then((view) => {
        if (active && view.summary) setHosting(view.summary);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  const cents = revenue?.confidence !== 'non_calculable' ? revenue?.cents : null;
  return (
    <>
      <article
        className="dashboard-summary-card dashboard-summary-card--client"
        aria-label="CA du mois en cours"
      >
        <div className="dashboard-summary-card__header">
          <span className="dashboard-summary-card__label">CA du mois en cours</span>
          <span className="dashboard-summary-card__icon">
            <PrototypeIcon name="wallet" />
          </span>
        </div>
        <strong title={revenue?.reason}>{cents == null ? '— €' : financialMoney(cents)}</strong>
        <small>
          Encaissé HT ·{' '}
          {new Intl.DateTimeFormat('fr-FR', {
            month: 'long',
            year: 'numeric',
            timeZone: 'Europe/Paris',
          }).format(new Date(month + '-01T12:00:00Z'))}
        </small>
        <small>
          {revenue?.confidence === 'incomplet'
            ? 'Sous-total connu · Finances & stats'
            : cents == null
              ? 'Données indisponibles'
              : 'Données Finances & stats'}
        </small>
      </article>
      <article
        className="dashboard-summary-card dashboard-summary-card--neutral"
        aria-label="Serveurs et sites"
      >
        <div className="dashboard-summary-card__header">
          <span className="dashboard-summary-card__label">Serveurs et sites</span>
          <span className="dashboard-summary-card__icon">
            <PrototypeIcon name="network" />
          </span>
        </div>
        <div className="dashboard-hosting-counts">
          <div>
            <strong>{hosting?.managedServers ?? '—'}</strong>
            <span>serveurs pilotés</span>
          </div>
          <div>
            <strong>{hosting?.administeredSites ?? '—'}</strong>
            <span>sites administrés</span>
          </div>
        </div>
      </article>
    </>
  );
}
